import React from "react";
import { useInfiniteScroll } from "@hooks/useInfiniteScroll";
import { NoData } from "@components/common/NoData/NoData";
import type { InfiniteScrollProps } from "./types/types";
import { useTranslation } from "react-i18next";

const InfiniteScroll = <T extends { id: string | number }>({
	items,
	renderItem,
	hasNextPage,
	fetchNextPage,
	isFetching,
	isLoading,
	noDataMessage,
	className = "px-[10px] sm:px-[20px] my-8 p-5 grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-2 grid-cols-1 gap-5",
	scrollRootId,
	externalScrollRoot = false,
	loadEnabled = true,
	loadingMessage,
}: InfiniteScrollProps<T>) => {
	useInfiniteScroll(isFetching, hasNextPage, fetchNextPage, scrollRootId, loadEnabled);
	const { t } = useTranslation();

	// Filter out undefined/null items to prevent errors
	const validItems = items.filter((item): item is T => item !== undefined && item !== null);

	// Use custom loading message if provided, otherwise use default translation
	const defaultLoadingMessage = loadingMessage || t("psdaCommon.common.loading");

	// Show loading state for initial load
	if (isLoading && validItems.length === 0) {
		return (
			<div className="flex justify-center items-center py-8">
				<span className={loadingMessage ? "animate-pulse" : ""}>{defaultLoadingMessage}</span>
			</div>
		);
	}

	if (validItems.length === 0) {
		return <NoData msg={noDataMessage} />;
	}

	return (
		<div id={externalScrollRoot ? undefined : scrollRootId} className={className}>
			{validItems.map((item, index) => (
				<React.Fragment key={item.id}>{renderItem(item, index)}</React.Fragment>
			))}

			{hasNextPage && loadEnabled && (
				<div className="col-span-full flex justify-center items-center py-4" style={{ minHeight: "48px" }}>
					{isFetching ? (
						<span className={loadingMessage ? "animate-pulse" : ""}>{defaultLoadingMessage}</span>
					) : null}
				</div>
			)}
		</div>
	);
};

export default InfiniteScroll;
